import { useState, useEffect } from "react";
import axios from "axios";
import useApiKey from "./useApiKey";

const APP_AUTH_EXPIRY_MINS = process.env.REACT_APP_AUTH_KEY_EXPIRY_MINS;
const BASE_URL = process.env.REACT_APP_REST_URL;
const APP_NAME = process.env.REACT_APP_REST_APP_NAME;

/**
 * Module-level variable — shared across ALL instances of this hook.
 * Guarantees only ONE interceptor is ever active on the global axios instance
 * at any time, regardless of how many components call useAxiosConfig().
 */
let activeInterceptorId = null;

const useAxiosConfig = () => {
  const [key, isLoading, error, reload] = useApiKey();
  const [axiosReady, setAxiosReady] = useState(false);

  useEffect(() => {
    let timeout = null;

    if (key && !isLoading && !error) {
      // Always eject any previously registered interceptor before adding a new one.
      // This handles: multiple hook instances mounting, token refreshes, re-renders.
      if (activeInterceptorId !== null) {
        axios.interceptors.request.eject(activeInterceptorId);
        activeInterceptorId = null;
      }

      axios.defaults.headers.common["Authorization"] = key;

      activeInterceptorId = axios.interceptors.request.use((config) => {
        const newConfig = { ...config };
        const re = /saml/gi;
        const re2 = /authenticate/gi;

        // Build absolute URL if a relative path was provided
        if (!(newConfig.url.indexOf("http") >= 0)) {
          if (newConfig.url.match(re) || newConfig.url.match(re2)) {
            newConfig.url = `${BASE_URL}/${newConfig.url}`;
          } else {
            newConfig.url = `${BASE_URL}${APP_NAME}/${
              newConfig.url.charAt(0) === "/"
                ? newConfig.url.substring(1)
                : newConfig.url
            }`;
          }
        }

        // Append API key to every non-auth request
        if (
          !(newConfig.url.indexOf("apiKey") >= 0) &&
          !newConfig.url.match(re) &&
          !newConfig.url.match(re2)
        ) {
          const delim = newConfig.url.indexOf("?") >= 0 ? "&" : "?";
          newConfig.url = newConfig.url + delim + `apiKey=${key}`;
        }

        return newConfig;
      });

      setAxiosReady(true);

      timeout = setTimeout(
        () => {
          console.log("API Key Expired — refreshing token");
          reload();
        },
        Number(APP_AUTH_EXPIRY_MINS) * 60 * 1000,
      );
    } else {
      setAxiosReady(false);
      if (error && error !== "missing API key token") {
        timeout = setTimeout(() => {
          console.log("Retrying API key fetch...");
          reload();
        }, 10 * 1000);
      } else if (error && error === "missing API key token") {
        setAxiosReady(true);
      }
    }

    return () => {
      clearTimeout(timeout);
      // Intentionally NOT ejecting on component unmount —
      // other components still rely on this interceptor.
      // It is only replaced when a new key is obtained (handled above).
    };
  }, [key, isLoading, reload, error]);

  return { loading: !axiosReady, error };
};

export default useAxiosConfig;
